import { Injectable, Injector, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from '@environments/environment';
import { jwtDecode } from 'jwt-decode';
import { CatalogCacheService } from './catalog-cache.service';
import { RouteReuseStoreService } from '../routing/route-reuse-store.service';
import { ListNavigationContextService } from '../routing/list-navigation-context.service';
import { ListRouteReattachService } from '../routing/list-route-reattach.service';
import { parseInternalReturnUrl } from '../utils/internal-return-url';

const TOKEN_KEY = 'billing_jwt';
const USER_KEY = 'billing_user';
const PERMISSIONS_KEY = 'billing_permisos';
const DEFAULT_ID_USER_KEY = 'billing_default_id_agente';

/** Módulo asociado a un permiso (puede ser null en algunos registros del backend). */
export interface LoginModulo {
  id_modulo: number;
  identificador: string;
  descripcion: string;
  img: string;
  id_parent: number | null;
  visible: boolean;
  posicion: number;
  grupo: number;
  only_root: boolean;
}

export interface UserPermiso {
  id_modulo: number;
  modulo: LoginModulo | null;
  lectura: boolean;
  escritura: boolean;
}

export interface LoginRol {
  id_rol: number;
  nombre: string;
  descripcion: string;
  user_permisos: UserPermiso[];
}

/** Datos del usuario devueltos por POST /api/login */
export interface LoginData {
  id_user: number;
  nombre: string;
  apellidos?: string;
  foto: string | null;
  /** Campo legacy; el login actual identifica por email. */
  user?: string;
  email: string;
  admin?: boolean;
  tecnico?: boolean;
  rol?: LoginRol;
  tlfno?: string;
  movil?: string;
  id_rol?: number;
  id_agente_padre?: number;
  id_agente_hijo?: number | null;
}

/** Token JWT devuelto en el login (formato nuevo). */
export interface LoginTokenPayload {
  access_token: string;
  expires_in: number;
  token_type: string;
}

export interface LoginResponse {
  data: LoginData;
  /** Puede ser string (legacy) u objeto con `access_token`. */
  token: string | LoginTokenPayload;
}

/** Mapa de permisos por identificador de módulo (`mod_cuenta`, etc.) o `id_modulo` en string si `modulo` es null. */
export type PermisosMap = Record<string, { read: boolean; write: boolean }>;

/** Usuario actual para el layout (nombre, email, avatar). */
export interface CurrentUser {
  name: string;
  email: string;
  avatar: string;
}

function getAccessTokenFromLoginResponse(res: LoginResponse): string | null {
  const t = res.token;
  if (typeof t === 'string' && t.length > 0) {
    return t;
  }
  if (t && typeof t === 'object' && 'access_token' in t) {
    const at = (t as LoginTokenPayload).access_token;
    return typeof at === 'string' && at.length > 0 ? at : null;
  }
  return null;
}

/** Construye el mapa de permisos: clave = `modulo.identificador` o `String(id_modulo)` si no hay módulo. */
export function buildPermisosMap(userPermisos: UserPermiso[] | undefined): PermisosMap {
  const out: PermisosMap = {};
  if (!userPermisos?.length) {
    return out;
  }
  for (const p of userPermisos) {
    const key = p.modulo?.identificador ?? String(p.id_modulo);
    const prev = out[key];
    out[key] = {
      read: !!(prev?.read || p.lectura),
      write: !!(prev?.write || p.escritura),
    };
  }
  return out;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = environment.apiUrl;
  private readonly googleAuthUrl = `${this.apiUrl}/api/auth/google`;
  private readonly loginUrl = `${this.apiUrl}/api/login`;
  private readonly injector = inject(Injector);

  constructor(
    private http: HttpClient,
    private router: Router,
  ) {}

  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }

  isLoggedIn(): boolean {
    return !!this.getToken();
  }

  /** Usuario actual para la barra (nombre, email, avatar). */
  getCurrentUser(): CurrentUser | null {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as CurrentUser;
    } catch {
      return null;
    }
  }

  private setSession(token: string, data: LoginData, permisos: PermisosMap): void {
    localStorage.setItem(TOKEN_KEY, token);
    const nameParts = [data.nombre, data.apellidos].filter(Boolean).join(' ').trim();
    const user: CurrentUser = {
      name: nameParts || data.user || '',
      email: data.email ?? '',
      avatar: data.foto ?? '',
    };
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(permisos));
    const idUser = data.id_user;
    if (idUser != null) {
      localStorage.setItem(DEFAULT_ID_USER_KEY, String(idUser));
    } else {
      localStorage.removeItem(DEFAULT_ID_USER_KEY);
    }
  }

  private readPermisosFromStorage(): PermisosMap {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) {
      return {};
    }
    try {
      return JSON.parse(raw) as PermisosMap;
    } catch {
      return {};
    }
  }

  /**
   * Indica si hay al menos un permiso persistido por clave de módulo.
   * Si el mapa está vacío (login sin `user_permisos`, SSO sin permisos, etc.), el menú no puede filtrar por módulo.
   */
  hasModulePermissionEntries(): boolean {
    return Object.keys(this.readPermisosFromStorage()).length > 0;
  }

  /** Comprueba si el usuario tiene permiso de lectura en el módulo (`identificador`, p. ej. `mod_cuenta`). */
  canRead(mod: string): boolean {
    return !!this.readPermisosFromStorage()[mod]?.read;
  }

  /** Comprueba si el usuario tiene permiso de escritura en el módulo. */
  canWrite(mod: string): boolean {
    return !!this.readPermisosFromStorage()[mod]?.write;
  }

  /** Login con usuario y contraseña. POST /api/login */
  login(user: string, password: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.loginUrl, { user, password }).pipe(
      tap((res) => {
        const accessToken = getAccessTokenFromLoginResponse(res);
        if (accessToken && res?.data) {
          const permisos = buildPermisosMap(res.data.rol?.user_permisos);
          this.setSession(accessToken, res.data, permisos);
        }
      }),
    );
  }

  /** Login con Google. POST /api/auth/google con el token JWT de la credencial. */
  loginWithGoogle(token: string): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(this.googleAuthUrl, { token }).pipe(
      tap((res) => {
        const accessToken = getAccessTokenFromLoginResponse(res);
        if (accessToken && res?.data) {
          const permisos = buildPermisosMap(res.data.rol?.user_permisos);
          this.setSession(accessToken, res.data, permisos);
        }
      }),
    );
  }

  /** Si `skipNavigation` es true, solo limpia sesión (p. ej. interceptor añade returnUrl al login). */
  logout(options?: { skipNavigation?: boolean }): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(DEFAULT_ID_USER_KEY);
    this.injector.get(CatalogCacheService).clearAll();
    this.injector.get(RouteReuseStoreService).clearAll();
    this.injector.get(ListNavigationContextService).clear();
    this.injector.get(ListRouteReattachService).clear();
    if (!options?.skipNavigation) {
      this.router.navigateByUrl('/login');
    }
  }

  /** ID de agente recibido en el login, para preselección en crear cuenta. */
  getDefaultIdUser(): number | null {
    const raw = localStorage.getItem(DEFAULT_ID_USER_KEY);
    if (raw == null) return null;
    const n = Number(raw);
    return Number.isInteger(n) ? n : null;
  }

  isTokenExpired(): boolean {
    const token = this.getToken();
    if (!token) return true;

    const decoded = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000);

    return (decoded.exp && decoded.exp < currentTime) || false;
  }

  /**
   * Cierra sesión y navega a `/login?expired=true` (con `returnUrl` interno si aplica).
   * Compartido por el interceptor HTTP y la comprobación al recuperar foco de la pestaña.
   */
  logoutDueToExpiredToken(currentUrl?: string | null): void {
    const returnUrl = parseInternalReturnUrl(currentUrl) ?? undefined;
    this.logout({ skipNavigation: true });
    void this.router.navigate(['/login'], {
      queryParams: {
        expired: 'true',
        ...(returnUrl ? { returnUrl } : {}),
      },
    });
  }

  /**
   * Si hay JWT en storage y está caducado, fuerza logout a login.
   * No actúa si no hay token (p. ej. ya en pantalla de login).
   * @returns `true` si se redirigió por caducidad.
   */
  checkSessionExpiredAndLogout(): boolean {
    if (!this.getToken()) {
      return false;
    }
    if (!this.isTokenExpired()) {
      return false;
    }
    this.logoutDueToExpiredToken(this.router.url);
    return true;
  }
}

